import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import { Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.min.js";

const Page = ({ pdf, num }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(num <= 2);
  const [ratio, setRatio] = useState(9 / 16);

  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setVisible(true), { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    let task;
    let cancelled = false;
    pdf.getPage(num).then((page) => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      setRatio(base.height / base.width);
      const canvas = ref.current.querySelector("canvas");
      const width = Math.min(ref.current.clientWidth * (window.devicePixelRatio || 1), 2400);
      const vp = page.getViewport({ scale: width / base.width });
      canvas.width = vp.width;
      canvas.height = vp.height;
      task = page.render({ canvasContext: canvas.getContext("2d"), viewport: vp });
    });
    return () => { cancelled = true; task?.cancel(); };
  }, [visible, pdf, num]);

  return (
    <div ref={ref} className="relative w-full bg-white" style={{ aspectRatio: `1 / ${ratio}` }} data-testid={`media-kit-page-${num}`}>
      <canvas className="block w-full h-full" />
    </div>
  );
};

export const PdfViewer = ({ url }) => {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(false);
    const task = pdfjs.getDocument({ url });
    task.promise.then((d) => alive && setPdf(d)).catch(() => alive && setError(true));
    return () => { alive = false; task.destroy(); };
  }, [url]);

  if (error) return null;
  if (!pdf) {
    return (
      <div className="py-32 flex items-center justify-center gap-2 text-stone-400 text-sm" data-testid="media-kit-loading">
        <Loader2 className="w-5 h-5 animate-spin" />Loading media kit…
      </div>
    );
  }
  return (
    <div className="space-y-1 sm:space-y-2" data-testid="media-kit-embed">
      {Array.from({ length: pdf.numPages }, (_, i) => <Page key={i} pdf={pdf} num={i + 1} />)}
    </div>
  );
};
